/*
 * Peel iPhone 18 — paid social landing page behaviour.
 * Loaded only on page templates with the "i18-paid-social-lp" suffix.
 *
 * One job: the bar that pins the model links under the site header once
 * the hero has scrolled away.
 *
 * The model gate itself needs no script. It used to: the two models were
 * toggle buttons that set a state and rewrote copy across the page, with
 * a button underneath to actually leave. That is two clicks, and the
 * second one lands on a button whose label just changed under the cursor.
 * Now each model is a link straight to that phone's cases, so naming the
 * model and acting on it are the same click, and the {model} token in the
 * copy is resolved by Liquid from a section setting. Nothing about which
 * phone the visitor has is client-side state any more.
 *
 * What is left is genuinely a script's job — knowing how far the page has
 * scrolled, and how tall the header currently is.
 */
(function () {
  'use strict';

  /* How far down the page the hero is considered gone. It is the hero's
     own minimum height rounded down, so the bar arrives as the hero
     leaves rather than over the top of it. */
  var PIN_AT = 560;

  function init() {
    var bar = document.querySelector('[data-peel-i18ps-bar]');
    if (!bar) return;

    var root = document.documentElement;
    var header = document.querySelector('header.peel-header');

    /* The bar is position:fixed, and fixed resolves against the nearest
       ancestor carrying a transform, filter, backdrop-filter, perspective
       or containment — not against the viewport. The bar is rendered
       inside the hero section so it can share that section's model
       blocks, which puts a section wrapper, <main>, and anything an app
       injects between it and <body>. Any one of those picking up a
       transform drops the bar into the middle of the page.

       Moving it to <body> removes the whole class of problem: there is
       then nothing between it and the root to establish a containing
       block. Done once, on init; the element itself is unchanged. */
    if (bar.parentNode !== document.body) {
      document.body.appendChild(bar);
    }

    /* The bar hangs off the bottom of the site header. Measure that edge
       rather than the header's height: the two are only the same when the
       header sits flush against the top of the viewport, and they are not
       while an announcement bar rides above it. getBoundingClientRect is
       in viewport coordinates, which is what a fixed offset wants. A
       header scrolled fully away gives a negative bottom, so clamp. */
    function measure() {
      var top = 0;
      if (header) {
        var box = header.getBoundingClientRect();
        if (box.height) top = Math.max(0, Math.round(box.bottom));
      }
      root.style.setProperty('--peel-i18ps-bar-top', top + 'px');
    }

    var pinned = false;

    function evaluate() {
      var y = window.pageYOffset || root.scrollTop || 0;
      var next = y > PIN_AT;
      if (next === pinned) return;
      pinned = next;
      bar.classList.toggle('is-pinned', pinned);
    }

    /* Both reads are cheap, but scroll fires often enough to be worth a
       frame. measure() runs on scroll as well as resize because the
       header's bottom edge moves while the announcement bar above it
       scrolls away, and a bar pinned to a stale offset overlaps it. */
    var queued = false;
    function onScroll() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        measure();
        evaluate();
      });
    }

    measure();
    evaluate();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
